<!--
  =====================================================================
  SECTION PRODUCT CARD - PRODUCT CATEGORY CARD
  =====================================================================
  
  Simple card component for displaying product category.
  
  FEATURES:
  - Fixed height card layout
  - Vertical bar animation on hover
  - Title and description text
  - Click navigation to product details
  - Responsive sizing
  
  BEHAVIOR:
  - Card shrinks/lifts on hover
  - Vertical bar animates from middle outward
  - Click anywhere on card navigates to product link
  
  =====================================================================
-->

<template>
  <div class="product-card" @click="goToProduct">
    
    <div class="vertical-bar"></div>

    <div class="card-content">
      <h2 class="card-title">{{ card.label }}</h2>
      <p class="card-text">{{ card.text }}</p>
    </div>
  </div>
</template>

<script setup>
// ───────────────────────────────────────────────────────────────────
// PROPS DEFINITION
// ───────────────────────────────────────────────────────────────────

/**
 * Component props definition
 * @prop {Object} card - Card data with label, text, link properties
 */
const props = defineProps({
  card: {
    type: Object,
    required: true
  }
})

// ───────────────────────────────────────────────────────────────────
// NAVIGATION HANDLER
// ───────────────────────────────────────────────────────────────────

/**
 * Navigates to product category page
 */
function goToProduct() {
  window.location.href = props.card.link
}
</script>

<style scoped lang="scss">
@import "@/assets/styles/partials/variables";

.product-card {
  position: relative;
  display: flex;
  background: $surface-base;
  color: $text-color;
  cursor: pointer;
  padding: $space-2 $space-2-5;
  height: 140px;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 2px 4px rgba($clr-brand-dark, 0.05);
  border: 1px solid $clr-border;

  @media (hover: hover) {
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba($clr-brand-dark, 0.12);
      border-color: rgba($clr-brand-dark, 0.2);
    }
  }

  /* ==================================================
     Vertical-bar con due pseudo-elementi
     ================================================== */
  .vertical-bar {
    position: absolute;
    left: 0;
    top: 0;
    width: 3px;
    height: 100%;
    pointer-events: none;

    &::before,
    &::after {
      content: "";
      position: absolute;
      left: 0;
      width: 3px;
      background: $clr-border;
      height: 0;
      transition: height 0.5s ease, background 0.5s ease;
    }

    /* pseudo-elemento che crescerà verso l'alto */
    &::before {
      bottom: 50%;
    }
    /* pseudo-elemento che crescerà verso il basso */
    &::after {
      top: 50%;
    }
  }

  /* All'hover, entrambe le pseudo-elementi si estendono a metà dell'altezza */
  @media (hover: hover) {
    &:hover .vertical-bar::before,
    &:hover .vertical-bar::after {
      height: 50%;
      background: $clr-brand-dark;
    }
  }
  /* ================================================== */

  .card-content {
    margin-left: $space-1-25;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .card-title {
    font-size: clamp(1.4rem, 2.5vw, 1.8rem);
    // margin: 0 0 $space-0-75;
    font-weight: 500;
    line-height: 1.2;
    font-family: 'Karla', sans-serif;
  }

  .card-text {
    font-size: clamp(0.95rem, 1.5vw, 1.1rem);
    color: $clr-text-light;
    margin: 0;
    line-height: 1.5;
  }
}

/* ------------------------------------------------------------------
   Media queries responsive - Display medio-grande (≤1300px)
------------------------------------------------------------------ */
@media (max-width: 1300px) {
  .product-card {
    padding: $space-1-75 $space-2;
    height: 130px;

    .card-title {
      font-size: clamp(1.3rem, 2.3vw, 1.6rem);
      margin-bottom: $space-0-5;
    }

    .card-text {
      font-size: clamp(0.9rem, 1.4vw, 1.05rem);
    }
  }
}

/* ------------------------------------------------------------------
   Media queries responsive - Tablet (≤920px)
------------------------------------------------------------------ */
@media (max-width: 920px) {
  .product-card {
    padding: $space-1-5 $space-1-75;
    height: 120px;

    .card-content {
      margin-left: $space-1;
    }

    .card-title {
      font-size: clamp(1.2rem, 2.2vw, 1.5rem);
    }

    .card-text {
      font-size: clamp(0.85rem, 1.3vw, 1rem);
    }
  }
}

/* ------------------------------------------------------------------
   Media queries responsive - Mobile (≤480px)
------------------------------------------------------------------ */
@media (max-width: 480px) {
  .product-card {
    padding: $space-1-25 $space-1-5;
    height: 110px;

    .card-content {
      margin-left: $space-0-75;
    }

    .card-title {
      font-size: clamp(1.1rem, 3vw, 1.3rem);
      margin-bottom: $space-0-25;
    }

    .card-text {
      font-size: clamp(0.8rem, 2.5vw, 0.95rem);
      line-height: 1.4;
    }

    @media (hover: hover) {
      &:hover {
        transform: translateY(-1px);
      }
    }
  }
}

/* ------------------------------------------------------------------
   Media queries responsive - Mobile molto piccolo (≤360px)
------------------------------------------------------------------ */
@media (max-width: 360px) {
  .product-card {
    padding: $space-1 $space-1-25;
    height: 100px;

    .card-title {
      font-size: clamp(1rem, 3.5vw, 1.2rem);
    }

    .card-text {
      font-size: clamp(0.75rem, 2.8vw, 0.9rem);
    }
  }
}
</style>